/**
 * LotSmith — Magic-link verification landing.
 *
 * User clicks link in their email → redirected here with ?token=... . We
 * POST the token to worker-auth /v1/auth/verify to establish the session
 * cookie, then redirect into /app. M3 stubs the verify to always succeed
 * so the UX is usable before worker-auth lands in M4.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

type State = "verifying" | "ok" | "error";

export function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>("verifying");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setError("Missing token");
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${apiBase()}/v1/auth/verify`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) throw new Error(`Verification failed (${res.status})`);
        setState("ok");
        setTimeout(() => navigate("/app", { replace: true }), 500);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn("[verify] API not available — dev stub success", err);
          setState("ok");
          setTimeout(() => navigate("/app", { replace: true }), 500);
          return;
        }
        setState("error");
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    })();
  }, [params, navigate]);

  return (
    <div className="auth">
      <Link to="/" className="auth__brand">LotSmith</Link>
      <div className="auth__card">
        {state === "verifying" && <p>Signing you in…</p>}
        {state === "ok" && <p>Signed in. Redirecting…</p>}
        {state === "error" && (
          <>
            <h1>Link invalid</h1>
            <p className="auth__lead">
              {error ?? "This sign-in link is invalid or expired."}
            </p>
            <Link to="/login" className="btn btn--primary btn--block">
              Request a new link
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function apiBase(): string {
  return (import.meta.env.VITE_AUTH_API_BASE as string | undefined) ?? "";
}
